import { useEffect, useState } from 'react'
import { ClipboardList, TrendingUp, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react'
import { supabase } from '../../lib/supabase'

const ESTADOS_FINAL_RESUELTO = ['Recibido tarde', 'En construcción', 'Construido por NVC']
const ESTADOS_FINAL_TERMINAL = ['No fue posible', 'Descartado']

// ────────────────────────────────────────────────────────────────────────
// DRLProgressBars
//
// Muestra las dos barras de progreso del DRL (Información necesaria) de
// una consultoría: estado INICIAL (al levantamiento) y estado FINAL (al
// cierre), con el delta de puntos recuperados después.
//
// Reglas de cómputo:
//   - Excluye items con estado='No aplica' del denominador
//   - "Recibido inicial" = estado='Recibido'
//   - "Resuelto final"   = estado='Recibido'
//                        OR estado_final IN ('Recibido tarde',
//                                            'En construcción',
//                                            'Construido por NVC')
//
// Modos de uso:
//   <DRLProgressBars items={array} />          ← compute from provided items
//   <DRLProgressBars consultancyId="uuid" />   ← fetch self
//
// Si la consultancy no tiene items, no renderiza nada (return null).
// ────────────────────────────────────────────────────────────────────────

function computeStats(items) {
  const aplica = items.filter(i => i.estado !== 'No aplica')
  const total = aplica.length
  const recibidoIni = aplica.filter(i => i.estado === 'Recibido').length

  // Final dividido en 2 segmentos:
  //   verde: Recibido (original) + Recibido tarde + Construido por NVC = DEFINITIVO
  //   azul:  En construcción = aún trabajándose, cuenta pero no es definitivo
  const recibidoFinal = aplica.filter(i =>
    i.estado === 'Recibido' ||
    i.estado_final === 'Recibido tarde' ||
    i.estado_final === 'Construido por NVC'
  ).length
  const enProcesoFinal = aplica.filter(i =>
    i.estado_final === 'En construcción' && i.estado !== 'Recibido'
  ).length
  const cerradoFin = recibidoFinal + enProcesoFinal

  // No resueltos: items en el denominador que NO cuentan como cerrados
  const noResueltos = aplica.filter(i =>
    i.estado !== 'Recibido' &&
    (!i.estado_final || ESTADOS_FINAL_TERMINAL.includes(i.estado_final))
  )

  return {
    total,
    recibidoIni,
    recibidoFinal,
    enProcesoFinal,
    cerradoFin,
    noResueltos,
    pctIni:           total > 0 ? Math.round((recibidoIni    / total) * 100) : 0,
    pctRecibidoFinal: total > 0 ? Math.round((recibidoFinal  / total) * 100) : 0,
    pctEnProceso:     total > 0 ? Math.round((enProcesoFinal / total) * 100) : 0,
    pctFin:           total > 0 ? Math.round((cerradoFin     / total) * 100) : 0,
  }
}

export default function DRLProgressBars({ consultancyId, items, compact = false }) {
  const [fetched, setFetched] = useState(items ?? null)
  // 'ini' | 'fin' | 'no_res' | null — solo una sección abierta a la vez
  const [openSection, setOpenSection] = useState(null)

  const toggleSection = (s) => setOpenSection(prev => prev === s ? null : s)

  useEffect(() => {
    if (items) { setFetched(items); return }
    if (!consultancyId) return
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('solicitudes_informacion')
        .select('id, descripcion, categoria, estado, estado_final, razon_no_cierre')
        .eq('consultancy_id', consultancyId)
      if (!cancelled) setFetched(data || [])
    })()
    return () => { cancelled = true }
  }, [consultancyId, items])

  if (fetched === null) {
    return <div className="text-xs text-gray-400 py-2">Cargando…</div>
  }
  if (fetched.length === 0) return null

  const s = computeStats(fetched)
  if (s.total === 0) return null

  const delta = s.pctFin - s.pctIni

  // ── Compact: pills en una línea (para cards de dashboard)
  if (compact) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">
          <ClipboardList size={10} /> Inicial {s.pctIni}%
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
          <TrendingUp size={10} /> Recibido {s.pctRecibidoFinal}%
        </span>
        {s.pctEnProceso > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">
            En proceso {s.pctEnProceso}%
          </span>
        )}
      </div>
    )
  }

  // ── Full: card con título + 2 barras + delta (para tabs y headers)
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList size={16} className="text-brand-700" />
          <h3 className="text-sm font-semibold text-gray-800">Información necesaria</h3>
        </div>
        <span className="text-xs text-gray-500 tabular-nums">{s.cerradoFin}/{s.total} resueltos</span>
      </div>

      {/* Barra inicial */}
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-600">Inicial <span className="text-gray-400">(al levantamiento)</span></span>
          <span className="font-semibold text-amber-700 tabular-nums">{s.pctIni}% recibido</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all"
            style={{ width: `${s.pctIni}%` }}
          />
        </div>
        <button
          onClick={() => toggleSection('ini')}
          className="mt-1 text-[10px] text-amber-700 hover:text-amber-900 font-medium inline-flex items-center gap-1 transition-colors"
        >
          {openSection === 'ini' ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          Ver detalle por estado inicial
        </button>
        {openSection === 'ini' && (
          <DetailList items={fetched} mode="inicial" />
        )}
      </div>

      {/* Barra final — stacked: verde (recibido) + azul (en proceso) */}
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-600">Final <span className="text-gray-400">(al cierre)</span></span>
          <span className="tabular-nums">
            <span className="font-semibold text-emerald-700">{s.pctRecibidoFinal}% recibido</span>
            {s.pctEnProceso > 0 && (
              <> · <span className="font-semibold text-blue-700">{s.pctEnProceso}% en proceso</span></>
            )}
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
          <div
            className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
            style={{ width: `${s.pctRecibidoFinal}%` }}
            title={`${s.recibidoFinal} items recibidos definitivamente`}
          />
          <div
            className="h-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all"
            style={{ width: `${s.pctEnProceso}%` }}
            title={`${s.enProcesoFinal} items en construcción`}
          />
        </div>
        {/* Mini-leyenda solo cuando hay split */}
        {s.pctEnProceso > 0 && (
          <div className="flex items-center gap-3 text-[10px] text-gray-500 mt-1">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm bg-emerald-500" /> Recibido ({s.recibidoFinal})
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-sm bg-blue-500" /> En proceso ({s.enProcesoFinal})
            </span>
          </div>
        )}
        <button
          onClick={() => toggleSection('fin')}
          className="mt-1 text-[10px] text-emerald-700 hover:text-emerald-900 font-medium inline-flex items-center gap-1 transition-colors"
        >
          {openSection === 'fin' ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          Ver detalle por estado final
        </button>
        {openSection === 'fin' && (
          <DetailList items={fetched} mode="final" />
        )}
      </div>

      {/* Delta de cierre */}
      {delta > 0 && (
        <div className="flex items-center justify-end gap-1 text-xs text-emerald-600 font-medium pt-1 border-t border-gray-50">
          <TrendingUp size={11} /> +{delta} pts recuperados después
        </div>
      )}

      {/* Lista expandible de no resueltos */}
      {s.noResueltos.length > 0 && (
        <div className="pt-2 border-t border-gray-100">
          <button
            onClick={() => toggleSection('no_res')}
            className="w-full flex items-center gap-2 text-xs font-semibold text-red-700 hover:text-red-900 transition-colors"
          >
            {openSection === 'no_res' ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <AlertCircle size={12} />
            Ver {s.noResueltos.length} item{s.noResueltos.length !== 1 ? 's' : ''} no resuelto{s.noResueltos.length !== 1 ? 's' : ''} ({100 - s.pctFin}%)
          </button>

          {openSection === 'no_res' && (
            <div className="mt-3 space-y-2">
              {s.noResueltos.map(item => {
                const terminal = ESTADOS_FINAL_TERMINAL.includes(item.estado_final)
                const pendingDecision = !item.estado_final
                return (
                  <div
                    key={item.id}
                    className={`px-3 py-2 rounded-md border ${terminal ? 'bg-red-50 border-red-100' : 'bg-amber-50 border-amber-100'}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-xs flex-shrink-0 mt-0.5">{terminal ? '✗' : '⏳'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-800">{item.descripcion}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          {item.categoria} · Inicial: {item.estado}
                          {item.estado_final && <> · Final: <strong>{item.estado_final}</strong></>}
                          {pendingDecision && <> · <strong className="text-amber-700">Pendiente decisión</strong></>}
                        </div>
                        {item.razon_no_cierre && (
                          <div className="text-[11px] italic text-red-800 mt-1.5 leading-snug">
                            "{item.razon_no_cierre}"
                          </div>
                        )}
                        {terminal && !item.razon_no_cierre && (
                          <div className="text-[10px] text-red-600 font-medium mt-1">
                            ⚠️ Falta capturar razón
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────
// DetailList — lista de items agrupados por estado (inicial o final)
// Reutilizable para los dos drilldowns desde las barras.
// ────────────────────────────────────────────────────────────────────────

const ESTADO_INICIAL_META = {
  'Recibido':        { color: 'emerald', icon: '✓', label: 'Recibido' },
  'Entrega parcial': { color: 'yellow',  icon: '◐', label: 'Entrega parcial' },
  'En revisión':     { color: 'blue',    icon: '◔', label: 'En revisión' },
  'Pendiente':       { color: 'amber',   icon: '⏳', label: 'Pendiente' },
  'No existe':       { color: 'red',     icon: '✗', label: 'No existe' },
  'No aplica':       { color: 'gray',    icon: '⊘', label: 'No aplica' },
}

const ESTADO_FINAL_META = {
  'Recibido':            { color: 'emerald', icon: '✓', label: 'Recibido original (sin cambios)' },
  'Recibido tarde':      { color: 'emerald', icon: '✓', label: 'Recibido tarde' },
  'Construido por NVC':  { color: 'emerald', icon: '🔨', label: 'Construido por NVC' },
  'En construcción':     { color: 'blue',    icon: '🔄', label: 'En construcción' },
  'Descartado':          { color: 'gray',    icon: '✗', label: 'Descartado' },
  'No fue posible':      { color: 'red',     icon: '✗', label: 'No fue posible' },
  '__sin_definir__':     { color: 'amber',   icon: '⏳', label: 'Sin decisión final' },
}

const COLOR_CLASS = {
  emerald: 'bg-emerald-50 border-emerald-100 text-emerald-900',
  yellow:  'bg-yellow-50 border-yellow-100 text-yellow-900',
  blue:    'bg-blue-50 border-blue-100 text-blue-900',
  amber:   'bg-amber-50 border-amber-100 text-amber-900',
  red:     'bg-red-50 border-red-100 text-red-900',
  gray:    'bg-gray-50 border-gray-100 text-gray-700',
}

function DetailList({ items, mode }) {
  // Agrupar items por estado (inicial o final)
  const buckets = {}
  const isFinal = mode === 'final'

  for (const item of items) {
    let key
    if (isFinal) {
      // Para "final", agrupamos por estado_final; pero los que tienen estado='Recibido'
      // original van en su propio bucket sin importar el estado_final.
      if (item.estado === 'Recibido' && !item.estado_final) {
        key = 'Recibido'
      } else if (item.estado_final) {
        key = item.estado_final
      } else if (item.estado === 'No aplica') {
        continue  // se excluyen del modo final
      } else {
        key = '__sin_definir__'
      }
    } else {
      key = item.estado
    }
    if (!buckets[key]) buckets[key] = []
    buckets[key].push(item)
  }

  // Orden estable de los buckets
  const meta = isFinal ? ESTADO_FINAL_META : ESTADO_INICIAL_META
  const orden = isFinal
    ? ['Recibido', 'Recibido tarde', 'Construido por NVC', 'En construcción', '__sin_definir__', 'Descartado', 'No fue posible']
    : ['Recibido', 'Entrega parcial', 'En revisión', 'Pendiente', 'No existe', 'No aplica']

  const groups = orden
    .filter(k => buckets[k]?.length)
    .map(k => ({ key: k, meta: meta[k], items: buckets[k] }))

  if (groups.length === 0) {
    return <div className="mt-3 text-[11px] text-gray-400">Sin items.</div>
  }

  return (
    <div className="mt-3 space-y-3">
      {groups.map(g => (
        <div key={g.key}>
          <div className={`text-[11px] font-semibold mb-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${COLOR_CLASS[g.meta.color]}`}>
            <span>{g.meta.icon}</span>
            <span>{g.meta.label}</span>
            <span className="font-bold tabular-nums">({g.items.length})</span>
          </div>
          <ul className="space-y-1 pl-1">
            {g.items.map(it => (
              <li key={it.id} className="text-[11px] text-gray-700 leading-snug">
                <span className="text-gray-400 mr-1">•</span>
                <span>{it.descripcion}</span>
                {it.categoria && (
                  <span className="text-[10px] text-gray-400 ml-1">— {it.categoria}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
